<script setup>
  import {ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {confirmationLabels} from "@pages/Patients/Enums/Enums.js";
  import {MedSpinner} from "@pharma4u/medicheck-components";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "ComponentSignatureHistoryDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
  });

  const refDialog = ref(null);
  const groupedConfirmations = ref([]);
  const loading = ref(false);

  function open() {
    loading.value = true;
    useAuthHttp().get(route("api.patients.confirmations", {patient: props.patient.id}), {
      onSuccess: (response) => {
        groupedConfirmations.value = response.groupedConfirmations;
      },
      onFinish: () => {
        loading.value = false;
      },
    });
    refDialog.value.open();
  }

  function close() {
    refDialog.value.close();
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-title="Verlauf der Einwilligungen"
    dialog-type="information"
    primary-label="Schließen"
    dialog-width="medium"
  >
    <template #content>
      <div v-if="loading" class="flex flex-col items-center justify-center py-10">
        <div class="py-4 text-lg">Einwilligungen werden geladen...</div>
        <MedSpinner size="2xl" :data-test="getTestId('signature-history-dialog-spinner')" />
      </div>

      <div v-else>
        <p>
          Hier wird Ihnen der Verlauf aller Einwilligungen und Widerrufe für diesen Patienten aufgelistet. Damit können
          Sie nachvollziehen, wann zu einem vergangenen Zeitpunkt Einwilligungen bestanden, die inzwischen widerrufen
          wurden.
        </p>
        <template v-for="(group, date) in groupedConfirmations" :key="date">
          <h1 class="mt-8 border-t-2 pt-4 text-xl">{{ group.date }}</h1>
          <ul class="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3">
            <li
              v-for="confirmation in group.confirmations"
              :key="confirmation.id"
              class="border-1 p-2 text-xs shadow-md"
            >
              <h3 class="text-sm font-semibold">
                {{ confirmationLabels[confirmation.subject] }}
              </h3>

              <template v-if="confirmation.type === 'accepted'">
                <span class="font-semibold text-mcgreen">Einwilligung</span>
              </template>
              <template v-else>
                <span class="font-semibold text-mcred">Widerruf</span>
              </template>

              {{
                confirmation.signer === "user"
                  ? "durch Apotheke"
                  : confirmation.signer === "patient"
                    ? "durch Patienten"
                    : "durch gesetzliche Vertretung"
              }}

              <div
                v-if="confirmation.signature"
                class="mt-2 aspect-video w-full bg-yellow-100 [&>svg]:h-auto [&>svg]:w-full"
                data-format="html"
                :data-encrypted="confirmation.signature"
              />
              <div v-else class="mt-2 grid aspect-video w-full place-items-center bg-gray-100 p-2 italic">
                ohne digitale Unterschrift
              </div>

              <div class="border-t-2 border-gray-700">
                <span
                  v-if="confirmation.city"
                  class="after:content-[',_'] last:after:content-['']"
                  :data-encrypted="confirmation.city"
                />
                <span
                  v-if="confirmation.created_at"
                  class="after:content-[',_'] last:after:content-['']"
                  :data-encrypted="confirmation.created_at"
                />
                <span
                  v-if="confirmation.signer_name"
                  class="after:content-[',_'] last:after:content-['']"
                  :data-encrypted="confirmation.signer_name"
                />
                <span
                  v-else-if="confirmation.signer === 'patient'"
                  class="after:content-[',_'] last:after:content-['']"
                >
                  <span :data-encrypted="patient.firstname" />
                  <span :data-encrypted="patient.lastname" />
                </span>
              </div>
            </li>
          </ul>
        </template>
      </div>
    </template>
  </ComponentDialog>
</template>
